import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Select } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { ExpertPicker } from '@/components/ExpertPicker';
import {
  SITE_MAP_KEY, getWriteRun, startWriteRun, steerWriteRun, writeRunItemAction, checkSitePageQuestions,
  type WriteRunState, type WriteRun, type WriteRunItem, type WriteRunItemState,
} from '@/data/client';

/* Write site (docs/bulk-write-plan.md, Phase C): the preflight, the button,
   and the run as it goes — three pages every five minutes, each batch in one
   commit to staging. Polls while a run is going. */

export const RUN_KEY = ['siteMap', 'writeRun'];
const errMsg = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const STATE: Record<WriteRunItemState, { label: string; variant: 'success' | 'accent' | 'warning' | 'neutral' | 'outline' }> = {
  queued: { label: 'Queued', variant: 'neutral' },
  writing: { label: 'Writing…', variant: 'accent' },
  pushed: { label: 'Pushed', variant: 'success' },
  held: { label: 'Held', variant: 'warning' },
  skipped: { label: 'Skipped', variant: 'outline' },
  failed: { label: 'Failed', variant: 'warning' },
  cancelled: { label: 'Cancelled', variant: 'outline' },
  restored: { label: 'Restored to scaffold', variant: 'outline' },
};
const fmt = (n: number | null) => (n == null ? '–' : n.toFixed(1));

export function WriteRunPanel({ onChanged }: { onChanged: () => void }) {
  const qc = useQueryClient();
  const { data, error } = useQuery({
    queryKey: RUN_KEY,
    queryFn: getWriteRun,
    // Polls while writing, and while a finished run waits to be read back from staging.
    refetchInterval: (q) => {
      const run = (q.state.data as WriteRunState | undefined)?.run;
      return run && (run.status === 'running' || (!run.scannedAt && run.counts.pushed > 0 && run.status !== 'paused')) ? 30_000 : false;
    },
  });
  const [expertId, setExpertId] = useState('');
  const [mode, setMode] = useState<'polished' | 'strict'>('polished');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(false);

  const act = async (fn: () => Promise<WriteRunState>) => {
    setBusy(true); setErr('');
    try { qc.setQueryData(RUN_KEY, await fn()); onChanged(); void qc.invalidateQueries({ queryKey: SITE_MAP_KEY, exact: true }); }
    catch (e) { setErr(errMsg(e, 'That did not work.')); }
    finally { setBusy(false); }
  };

  if (error) return <p className="gen-note smb__note is-err">{errMsg(error, 'Could not load the run.')}</p>;
  if (!data) return null;
  const { preflight: pf, run } = data;
  const active = run && (run.status === 'running' || run.status === 'paused');

  return (
    <div className="wb__run">
      {err && <p className="gen-note smb__note is-err">{err}</p>}
      {active ? <RunProgress run={run} busy={busy} act={act} /> : (
        <div className="wb__preflight">
          <p className="wb__runline">
            <strong>{pf.toWrite.length ? `Write ${plural(pf.toWrite.length, 'page')}` : 'Nothing ready to write'}</strong>
            {pf.toWrite.length > 0 && <span className="dash-muted"> · about {pf.minutes} minutes in {plural(pf.ticks, 'commit')} to staging{pf.costUsd != null ? ` · about $${pf.costUsd.toFixed(2)}` : ''}</span>}
          </p>
          {pf.blockers.map((b) => <p key={b} className="wb__warn wb__warn--inline"><Icon name="alert-circle" size={13} /> {b}</p>)}
          {pf.canStart && (
            <ul className="wb__notes">
              {pf.questionsOut > 0 && <li>{plural(pf.questionsOut, 'page')} still {pf.questionsOut === 1 ? 'has' : 'have'} questions out; they are written anyway with what the Vault has.</li>}
              {pf.forecastThin > 0 && <li>{plural(pf.forecastThin, 'page')} {pf.forecastThin === 1 ? 'is' : 'are'} forecast thin.</li>}
              {pf.noNotes > 0 && <li>{plural(pf.noNotes, 'scaffold')} {pf.noNotes === 1 ? 'has' : 'have'} no section notes, so the writer cannot follow the design's limits there.</li>}
              {pf.held > 0 && <li>{plural(pf.held, 'held page')} {pf.held === 1 ? 'is' : 'are'} left out.</li>}
              {pf.incomplete > 0 && <li>{plural(pf.incomplete, 'scaffolded page')} without a complete plan {pf.incomplete === 1 ? 'is' : 'are'} left out.</li>}
            </ul>
          )}
          {pf.canStart && (
            <div className="wb__runform">
              <ExpertPicker value={expertId} onChange={setExpertId} label="Signed by" disabled={busy} approvedOnly />
              <Select label="Grounding" value={mode} disabled={busy} onChange={(e) => setMode(e.target.value as 'polished' | 'strict')} options={[{ value: 'polished', label: 'Polished' }, { value: 'strict', label: 'Strict — [NEEDS: …] where a detail is missing' }]} />
              <Button variant="primary" size="sm" disabled={busy} onClick={() => { if (confirm(`Write and push ${plural(pf.toWrite.length, 'page')} to staging? Pages are written three at a time; you can pause or cancel.`)) void act(() => startWriteRun({ expertId: expertId || undefined, mode })); }} iconLeft={<Icon name="edit" size={13} />}>Write site</Button>
            </div>
          )}
          {run && !run.scannedAt && run.counts.pushed > 0 && (
            <p className="dash-muted smb__meta" style={{ margin: 0 }}>Reading the pushed pages back from staging about fifteen minutes after the run, once the site has built. Page Health scores what actually rendered, and the email goes out with those scores.</p>
          )}
          {run && (
            <div className="wb__lastrun">
              <button type="button" className="smb__link" onClick={() => setOpen((v) => !v)}>
                Last run {run.status === 'done' ? 'finished' : run.status} {(run.finishedAt || run.createdAt).slice(0, 16).replace('T', ' ')}: {summary(run)} {open ? '▾' : '▸'}
              </button>
              {open && <RunItems run={run} busy={busy} act={act} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function summary(run: WriteRun): string {
  const c = run.counts;
  return [c.pushed && `${c.pushed} pushed`, c.held && `${c.held} held`, c.failed && `${c.failed} failed`, c.skipped && `${c.skipped} skipped`, c.cancelled && `${c.cancelled} cancelled`, c.restored && `${c.restored} restored`]
    .filter(Boolean).join(', ') || 'nothing written';
}

function RunProgress({ run, busy, act }: { run: WriteRun; busy: boolean; act: (fn: () => Promise<WriteRunState>) => Promise<void> }) {
  const total = run.items.length;
  const done = total - run.counts.queued - run.counts.writing;
  return (
    <div className="wb__preflight">
      <p className="wb__runline">
        <strong>{run.status === 'paused' ? 'Paused' : 'Writing the site'}: {done} of {total}</strong>
        <span className="dash-muted"> · {summary(run)} · three pages every five minutes, each batch one commit</span>
      </p>
      <div className="wb__runform">
        {run.status === 'running'
          ? <Button variant="outline" size="sm" disabled={busy} onClick={() => act(() => steerWriteRun(run.id, 'pause'))}>Pause</Button>
          : <Button variant="outline" size="sm" disabled={busy} onClick={() => act(() => steerWriteRun(run.id, 'resume'))}>Resume</Button>}
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => { if (confirm('Cancel the rest of this run? Pages already pushed stay; a batch being written now still lands.')) void act(() => steerWriteRun(run.id, 'cancel')); }}>Cancel the rest</Button>
      </div>
      <RunItems run={run} busy={busy} act={act} />
    </div>
  );
}

function RunItems({ run, busy, act }: { run: WriteRun; busy: boolean; act: (fn: () => Promise<WriteRunState>) => Promise<void> }) {
  return (
    <ol className="wb__runitems">
      {run.items.map((i) => <RunItemLine key={i.id} item={i} busy={busy} act={act} />)}
    </ol>
  );
}

function RunItemLine({ item: i, busy, act }: { item: WriteRunItem; busy: boolean; act: (fn: () => Promise<WriteRunState>) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState('');
  const canRetry = ['failed', 'held', 'skipped', 'cancelled'].includes(i.state);
  const best = i.scoreLive ?? i.scoreFinal;
  const thin = i.state === 'pushed' && best != null && best < 5;
  // The file scored one thing and the rendered page another: something in the design dropped or hid the copy.
  const drift = i.scoreLive != null && i.scoreFinal != null && Math.abs(i.scoreLive - i.scoreFinal) >= 2;
  const followUp = async () => {
    if (!i.pageId) return;
    setChecking(true); setChecked('');
    try {
      const r = await checkSitePageQuestions(i.pageId);
      setChecked(r.result ? `${r.result.followups ?? 0} follow-up question${r.result.followups === 1 ? '' : 's'} written; send them from the page's questions in the site map.` : 'Checked.');
    } catch (e) { setChecked(errMsg(e, 'Could not check the Vault.')); }
    finally { setChecking(false); }
  };
  return (
    <li>
      <div className="wb__signals">
        <strong>{i.name}</strong>
        <code className="dash-muted">{i.route === '/' ? '/' : i.route.replace(/\/$/, '')}</code>
        <Badge variant={STATE[i.state].variant}>{STATE[i.state].label}</Badge>
        {i.scoreFinal != null && <span className="smb__flag" title="Page Health rank (effort and originality) of the file before pushing">before push {fmt(i.scoreFinal)}{i.revised ? ` (first draft ${fmt(i.scoreFirst)})` : ''}</span>}
        {i.scoreLive != null && <span className={`smb__flag${thin ? ' smb__flag--warn' : ''}`} title="Page Health's score for the page as it rendered on staging">on staging {fmt(i.scoreLive)}</span>}
        {i.state === 'pushed' && i.scanError && <span className="smb__flag smb__flag--warn" title={i.scanError}>{i.scannedAt ? 'could not read on staging' : 'waiting for staging'}</span>}
        {thin && i.pageId && <button type="button" className="smb__link" disabled={busy || checking} onClick={followUp}>{checking ? 'Checking the Vault…' : 'Check the Vault for follow-ups'}</button>}
        {i.commitUrl && <a className="smb__flag" href={i.commitUrl} target="_blank" rel="noreferrer">commit</a>}
        {(i.notes.length > 0 || i.error) && <button type="button" className="smb__link" onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : i.error ? 'Why' : 'Notes'}</button>}
        {canRetry && <button type="button" className="smb__link" disabled={busy} onClick={() => act(() => writeRunItemAction(i.id, 'retry'))}>Retry</button>}
        {i.canRestore && <button type="button" className="smb__link" disabled={busy} onClick={() => { if (confirm(`Put ${i.name} back to its scaffold? The written page is replaced in one commit.`)) void act(() => writeRunItemAction(i.id, 'restore')); }}>Restore the scaffold</button>}
      </div>
      {drift && <p className="wb__thin"><Icon name="alert-circle" size={12} /> Scored {fmt(i.scoreFinal)} as a file but {fmt(i.scoreLive)} on staging: check how the page renders, in case a component drops or hides copy.</p>}
      {checked && <p className="dash-muted smb__meta" style={{ margin: 0 }}>{checked}</p>}
      {open && (
        <ul className="wb__notes">
          {i.scanError && <li>Staging: {i.scanError}</li>}
          {i.error && <li>{i.error}</li>}
          {i.notes.map((n) => <li key={n}>{n}</li>)}
        </ul>
      )}
    </li>
  );
}
